import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/MemberstackAuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import ContactDialog from '../components/ContactDialog';
import { Check, Lock } from 'lucide-react';
import memberstack from '../lib/memberstack';

const BOKR_STANDARD_PRICE_ID = 'prc_bokr-standard-monthly-1d12a0uef';

export default function SignupPage() {
  const [loading, setLoading] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('standard');
  const { checkAuth } = useAuth();
  const navigate = useNavigate();

  const plans = [
    {
      id: 'standard',
      name: 'Bõkr Standard',
      price: '$15',
      period: 'NZD/month',
      description: 'Everything you need to manage your bookings',
      priceId: BOKR_STANDARD_PRICE_ID,
      available: true,
      features: [
        { text: 'Unlimited bookings & clients', included: true },
        { text: 'Invoice management & tracking', included: true },
        { text: 'Calendar with drag & drop', included: true },
        { text: 'Credit & prepayment tracking', included: true },
        { text: 'Email notifications', included: true },
        { text: 'SMS reminders', included: false, plusFeature: true },
      ],
    },
    {
      id: 'plus',
      name: 'Bõkr Plus',
      price: '$25',
      period: 'NZD/month',
      description: 'Advanced features for growing businesses',
      priceId: null,
      available: false,
      comingSoon: true,
      features: [
        { text: 'Everything in Standard', included: true },
        { text: 'SMS notifications', included: true },
        { text: 'Client self-booking portal', included: true },
        { text: 'Automated reminders', included: true },
        { text: 'Priority support', included: true },
      ],
    },
  ];

  const handleSignup = async () => {
    if (selectedPlan === 'plus') {
      toast.info('Bõkr Plus is coming soon! Please select Standard for now.');
      return;
    }

    setLoading(true);
    try {
      // First, open signup modal to create account
      const signupResult = await memberstack.openModal('SIGNUP');
      console.log('Signup result:', signupResult);
      
      // Hide the modal after successful signup
      memberstack.hideModal();
      
      if (signupResult?.data) {
        toast.success('Account created! Redirecting to checkout...');
        
        // Small delay to ensure account is fully created
        await new Promise(resolve => setTimeout(resolve, 500));
        
        // Now open checkout for the selected plan
        try {
          const checkoutResult = await memberstack.purchasePlansWithCheckout({
            priceId: BOKR_STANDARD_PRICE_ID
          });
          
          console.log('Checkout result:', checkoutResult);
          
          // Refresh auth status after purchase
          await checkAuth();
          
          toast.success('Subscription activated! Welcome to Bõkr');
          window.location.href = '/app';
        } catch (checkoutErr) {
          console.error('Checkout error:', checkoutErr);
          if (checkoutErr.message !== 'Modal closed' && checkoutErr.message !== 'POPUP_CLOSED') {
            // User created account but didn't complete payment - redirect to subscribe
            toast.info('Please complete your subscription to continue.');
            navigate('/subscribe');
          } else {
            // User closed checkout - redirect to subscribe page
            navigate('/subscribe');
          }
        }
      }
    } catch (err) {
      console.error('Signup error:', err);
      if (err.message !== 'Modal closed') {
        toast.error('Signup failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <div className="flex-1 flex flex-col items-center justify-center p-6 md:p-8">
        <div className="w-full max-w-4xl space-y-8 animate-in">
          {/* Header */}
          <div className="text-center">
            <h1 className="text-4xl font-heading font-bold tracking-tight">Bõkr</h1>
            <p className="text-muted-foreground mt-2">Choose your plan to get started</p>
          </div>

          {/* Plan Cards */}
          <div className="grid md:grid-cols-2 gap-6">
            {plans.map((plan) => (
              <div
                key={plan.id}
                onClick={() => plan.available && setSelectedPlan(plan.id)}
                className={`
                  relative border rounded-xl p-6 transition-all cursor-pointer
                  ${plan.available ? 'hover:border-primary hover:shadow-md' : 'opacity-60 cursor-not-allowed'}
                  ${selectedPlan === plan.id && plan.available ? 'border-primary ring-2 ring-primary/20 bg-primary/5' : 'bg-card'}
                `}
                data-testid={`plan-card-${plan.id}`}
              >
                {/* Coming Soon Badge */}
                {plan.comingSoon && (
                  <div className="absolute -top-3 right-4 px-3 py-1 bg-muted text-muted-foreground text-xs font-medium rounded-full flex items-center gap-1">
                    <Lock className="w-3 h-3" />
                    Coming Soon
                  </div>
                )}

                {/* Selected Indicator */}
                {selectedPlan === plan.id && plan.available && (
                  <div className="absolute top-4 right-4 w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-primary-foreground" />
                  </div>
                )}

                <div className="space-y-4">
                  {/* Plan Header */}
                  <div>
                    <h2 className="text-xl font-semibold">{plan.name}</h2>
                    <p className="text-muted-foreground text-sm mt-1">{plan.description}</p>
                  </div>

                  {/* Price */}
                  <div>
                    <span className="text-3xl font-bold">{plan.price}</span>
                    <span className="text-muted-foreground text-sm ml-1">{plan.period}</span>
                  </div>

                  {/* Features */}
                  <ul className="space-y-2 text-sm">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className={`flex items-center gap-2 ${feature.plusFeature ? 'opacity-50' : ''}`}>
                        {feature.included ? (
                          <Check className={`w-4 h-4 flex-shrink-0 ${plan.available ? 'text-green-500' : 'text-muted-foreground'}`} />
                        ) : (
                          <Lock className="w-4 h-4 flex-shrink-0 text-muted-foreground" />
                        )}
                        <span className={`${!feature.included || !plan.available ? 'text-muted-foreground' : ''}`}>
                          {feature.text}
                        </span>
                        {feature.plusFeature && (
                          <span className="ml-auto text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                            Plus
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          {/* Action Button */}
          <div className="max-w-md mx-auto space-y-4">
            <Button
              onClick={handleSignup}
              className="w-full"
              size="lg"
              disabled={loading || selectedPlan === 'plus'}
              data-testid="signup-button"
            >
              {loading ? 'Creating account...' : 'Create Account & Subscribe'}
            </Button>

            <p className="text-xs text-muted-foreground text-center">
              By creating an account, you agree to our{' '}
              <a href="/terms-of-service" className="text-primary hover:underline">
                Terms of Service
              </a>
              {' '}and{' '}
              <a href="/privacy-policy" className="text-primary hover:underline">
                Privacy Policy
              </a>
            </p>

            <p className="text-center text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link to="/login" className="text-primary hover:underline" data-testid="login-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-4 px-8 border-t border-border bg-background">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-6 text-xs text-muted-foreground">
          <span>© 2026 GetBokr. Operated by Lifely Holdings Limited (NZBN 9429053450883).</span>
          <a href="/privacy-policy" className="hover:text-foreground transition-colors">
            Privacy Policy
          </a>
          <a href="/terms-of-service" className="hover:text-foreground transition-colors">
            Terms of Service
          </a>
          <button onClick={() => setContactOpen(true)} className="hover:text-foreground transition-colors">
            Contact
          </button>
        </div>
      </footer>

      <ContactDialog open={contactOpen} onOpenChange={setContactOpen} />
    </div>
  );
}
